import React, { useEffect, useMemo, useState } from 'react';
import apiServiceCompany from '../../services/company.services';
import { Typography } from '@mui/material';
import { useCompanyId } from '../../services/CompanyidProvider';
import { MaterialReactTable } from 'material-react-table';
import { FluxChip } from '@meltwater/flux-web-components-react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import JsonToMaterialTable from '../../services/json.services';
import styles from './companydetail.module.css';
import {
  Box,
  Button,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Snackbar,
  Alert
} from '@mui/material';

const DEFAULT_PAGE_SIZE = 25;

const Companydetail = () => {
  const [allEntitlements, setAllEntitlements] = useState(null);
  const [filteredEntitlements, setFilteredEntitlements] = useState(null);
  const { companyId } = useCompanyId();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [totalCount, setTotalCount] = useState(0);
  const [globalFilter, setGlobalFilter] = useState('');
 
  const entcolumns = useMemo(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        size: 300,
      },
      {
        header: 'Value',
        accessorFn: ((row) => {
          return `${row.value}`
        }),
        size: 100,
        Cell: ({ cell, index }) => {
          return <div id={index}>{cell.getValue()}</div>;
        }, 
      },
      {
        header: 'Tags',
        accessorFn: ((row) => {
          return (
            <Box className={styles.chipContainer}>
              {row.tags.map((onetag, index) => (
                <FluxChip 
                  key={`${onetag}-${index}`}
                  swatchColor='green' 
                  displayOnly 
                  dense
                >
                  {onetag}
                </FluxChip>
              ))}
            </Box>
          );
        }),
        size: 200,
      },
      {
        header: 'Descriptions',
        accessorKey: 'description',
      },
    ],
    [],
  );
  
  useEffect(() => {
    if (allEntitlements) {
      if (!globalFilter) {
        setFilteredEntitlements(allEntitlements);
        setTotalCount(allEntitlements.length);
      } else {
        const lowercaseFilter = globalFilter.toLowerCase();
        const filtered = allEntitlements.filter(item => {
          if (item.name && item.name.toLowerCase().includes(lowercaseFilter)) return true;
          if (item.value && String(item.value).toLowerCase().includes(lowercaseFilter)) return true;
          if (item.description && item.description.toLowerCase().includes(lowercaseFilter)) return true;
          if (item.tags && Array.isArray(item.tags) && item.tags.some(tag => 
            tag.toLowerCase().includes(lowercaseFilter)
          )) return true;
          
          return false;
        });
        setFilteredEntitlements(filtered);
        setTotalCount(filtered.length);
      }
      setPage(1);
    }
  }, [globalFilter, allEntitlements]);
  
  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(totalCount / pageSize);
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleManualRefresh = () => {
    setIsRefreshing(true);
    fetchDescriptionsAndEntitlements().then(() => {
      setIsRefreshing(false);
      setSnackbarMessage('Data has been refreshed');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    }).catch(error => {
      setIsRefreshing(false);
      setSnackbarMessage('Error refreshing data: ' + error.message);
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };
   
  const fetchDescriptionsAndEntitlements = async () => {
    if (!companyId) return Promise.resolve();
    
    setIsLoading(true);
    try {
      const companyEntitlmentdescr = await apiServiceCompany.getCompanyEntitlementsDescr(companyId);
      const companyEntitlment = await apiServiceCompany.getCompanyEntitlements(companyId);
      const jsonArray = JsonToMaterialTable(companyEntitlment.data);
      const finalArray = jsonArray.map((oneItem) => {
        const descr = companyEntitlmentdescr.data[`${oneItem.name}`] || { description: 'No description available' };
        oneItem['description'] = descr.description;
        return oneItem;
      });
      
      setAllEntitlements(finalArray);
      setFilteredEntitlements(finalArray);
      setTotalCount(finalArray.length);
      
    } catch (error) {
      console.log(`FAIL`, error);
      setSnackbarMessage('Error fetching data: ' + error.message);
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
    
    return Promise.resolve();
  };

  useEffect(() => {
    fetchDescriptionsAndEntitlements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);
  
  const isNextDisabled = useMemo(() => {
    if (!filteredEntitlements) return true;
    return page * pageSize >= totalCount;
  }, [page, pageSize, totalCount, filteredEntitlements]);

  const renderPagination = () => {
    return (
      <div className={styles.paginationContainer}>
        <div className={styles.paginationLeft}>
          <Button 
            variant="contained"
            onClick={handlePrevPage}
            disabled={page === 1}
            className={styles.paginationButton}
          >
            Previous Page
          </Button>
          
          <Chip 
            label={`Page ${page} of ${Math.ceil(totalCount / pageSize) || 1}`} 
            variant="outlined"
            className={styles.pageIndicator}
          />
        </div>
        
        <div className={styles.paginationRight}>
          <Button
            variant="outlined"
            onClick={handleManualRefresh}
            startIcon={<RefreshIcon className={isRefreshing ? styles.spinIcon : ''} />}
            disabled={isRefreshing}
            className={styles.refreshButton}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          
          <FormControl className={styles.pageSizeSelector} size="small">
            <InputLabel id="page-size-select-label">Page Size</InputLabel>
            <Select
              labelId="page-size-select-label"
              id="page-size-select"
              value={pageSize}
              label="Page Size"
              onChange={handlePageSizeChange}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={15}>15</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
          
          <Button 
            variant="contained"
            onClick={handleNextPage}
            disabled={isNextDisabled}
            className={styles.paginationButton}
          >
            Next Page
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <div className={styles.headerRow}>
          <Button 
            className={styles.backButton}
            onClick={() => window.history.back()}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
          <Typography variant="h6" color="black" className={styles.tableHeader}>
            Entitlements
          </Typography>
        </div>
      </div>
      
      <Paper className={styles.tableContainer}>
        {renderPagination()}
        
        {(isLoading || isRefreshing) && <LinearProgress sx={{ mt: 1 }} />}
        
        {filteredEntitlements && (
          <div className={styles.tableWrapper}>
            <MaterialReactTable
              columns={entcolumns}
              data={filteredEntitlements}
              enableColumnOrdering
              enableColumnActions
              enableGlobalFilter
              onGlobalFilterChange={setGlobalFilter}
              globalFilterFn="contains"
              enableColumnFilters
              enableStickyHeader
              enableRowActions={false}
              enablePagination
              manualPagination={false}
              rowCount={totalCount}
              muiTableContainerProps={{ 
                className: styles.expandableTable
              }}
              muiTablePaperProps={{ elevation: 0 }}
              muiSearchTextFieldProps={{
                variant: 'outlined',
                size: 'small',
                placeholder: 'Search all entitlements...',
                className: styles.searchField
              }}
              state={{
                globalFilter,
                pagination: {
                  pageIndex: page - 1,
                  pageSize: pageSize,
                }
              }}
              onPaginationChange={(updater) => {
                const newPagination = typeof updater === 'function' 
                  ? updater({ pageIndex: page - 1, pageSize }) 
                  : updater;
                setPage(newPagination.pageIndex + 1);
                setPageSize(newPagination.pageSize);
              }}
              initialState={{ 
                density: 'compact',
                columnVisibility: { id: true }
              }}
              muiTableBodyProps={{
                sx: {
                  '& tr': {
                    visibility: 'visible',
                  },
                }
              }}
            />
          </div>
        )}
      </Paper>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Companydetail;
